import {
  CUSTOM_CLIENT_ENTRIES,
  type CustomClientEntryItem,
} from '@/_constants/questionnaires';
import { sectionTypes } from '@/_constants/sections';
import { UNTRIGGERABLE_QUESTION_SNIPPET } from '@/_constants/questions';
import { getParentQuestion, isQuestionTriggered } from '@/_utils/questions';
import { sectionTriggered } from '@/_utils/sections';
import type { Question, Questionnaire } from '@/_types/models/questionnaire';
import type { Section } from '@/_types/models/sections';

export const sortClientQuestionnaires = (
  a: Questionnaire,
  b: Questionnaire,
) => {
  const getStatusPriority = (item: Questionnaire) => {
    if (item.locked) return 2;
    if (item.completed) return 1;

    return 0;
  };

  const statusA = getStatusPriority(a);
  const statusB = getStatusPriority(b);

  if (statusA !== statusB) return statusA - statusB;

  const dateA = new Date(a.due_date || a.created_at).getTime();
  const dateB = new Date(b.due_date || b.created_at).getTime();

  return dateA - dateB;
};

// TODO @chris these functions are from Vue and needs to be cleaned up

export const findSpecialSection = (
  section: Section,
): CustomClientEntryItem | undefined => {
  const isSpecialSection: boolean = getIsSpecialSection(section);

  if (!isSpecialSection) return undefined;

  let specialSection = CUSTOM_CLIENT_ENTRIES.find(
    (s) => s.name === section.name,
  );

  if (!specialSection) {
    specialSection = CUSTOM_CLIENT_ENTRIES.find((s) =>
      s.otherNames.includes(section.name),
    );
  }

  return specialSection;
};

export const getIsSpecialSection = (section: Section | undefined): boolean => {
  if (!section) return false;

  return CUSTOM_CLIENT_ENTRIES.map((s) => s.name)
    .concat(CUSTOM_CLIENT_ENTRIES.map((s) => s.otherNames).flat())
    .includes(section?.name);
};

export const getSectionQuestionsToBeAnswered = (
  section: Section,
  question: Question,
) => {
  if (!question.follow_up_question_id) return true;
  if (!getParentQuestion(section, question)) return false;

  if (getParentQuestion(section, question)?.follow_up_question_id) {
    return (
      isQuestionTriggered(section, getParentQuestion(section, question)) &&
      isQuestionTriggered(section, question) &&
      getParentQuestion(section, question)?.question_type !== 'entries'
    );
  }

  return (
    isQuestionTriggered(section, question) &&
    getParentQuestion(section, question)?.question_type !== 'entries'
  );
};

export const getFilteredSectionQuestionsToBeAnswered = (
  questionnaire: any,
  section: any,
): Array<Question> => {
  if (questionnaire.show_uploads_as_document_checklist) {
    return section?.questionnaire_questions?.filter(
      (q: any) =>
        q.question_type !== 'upload' &&
        getSectionQuestionsToBeAnswered(section, q),
    );
  } else {
    return section?.questionnaire_questions?.filter((q: any) =>
      getSectionQuestionsToBeAnswered(section, q),
    );
  }
};

export const getDocumentChecklistItemsForSection = (section: any) => {
  return section.questionnaire_questions.filter((q: any) => {
    return (
      q.question_type === 'upload' &&
      getSectionQuestionsToBeAnswered(section, q)
    );
  });
};

export const matchSection = (sectionType: any) => {
  const section = sectionTypes.find((t: any) => t.id === sectionType);

  if (section) {
    return section;
  } else {
    return sectionTypes.find((t: any) => t.key === 'custom');
  }
};

export const getSectionKey = (section: Section) => {
  return matchSection(section.section_type)?.key;
};

export const getAllDocumentChecklistItems = (questionnaire?: Questionnaire) => {
  if (!questionnaire?.sections?.length) return [];

  const { sections } = questionnaire;

  if (sections) {
    const allQuestions = sections
      .map((section: Section) => section.questionnaire_questions)
      .flat() as Question[];

    return sections
      .filter((section: Section) => {
        const triggerQuestion = allQuestions?.find(
          (q) => q?.id === section.trigger_question_id,
        );
        return (
          getSectionKey(section) === 'questionnaire' &&
          (sectionTriggered(section, sections, allQuestions) ||
            triggerQuestion?.text.includes(UNTRIGGERABLE_QUESTION_SNIPPET))
        );
      })
      .map((section: Section) => getDocumentChecklistItemsForSection(section))
      .flat();
  } else {
    return [];
  }
};

export const getSelectableScheduleLineItems = (
  scheduleName: string,
): Array<{
  label: string;
  value: string;
}> => {
  return CUSTOM_CLIENT_ENTRIES.filter((section) => {
    if (typeof section.form !== 'undefined') {
      return scheduleName.includes(section.form);
    } else {
      return false;
    }
  })
    .flatMap((section) => {
      return section.sections.flatMap((subSection) => {
        return subSection.fields;
      });
    })
    .map((field) => {
      return {
        value: field,
        label: field,
      };
    });
};

export const getUploadPath = (questionnaire: Questionnaire) => {
  let uploadPath: string | null | undefined = questionnaire?.document_path;

  if (!uploadPath) return '';

  uploadPath = uploadPath.replace('{year}', String(questionnaire?.tax_year));

  uploadPath = uploadPath.replace(
    '{questionnaire_name}',
    String(questionnaire?.formatted_name),
  );

  return uploadPath;
};
